/**
 * As notificações dentro do APLICATIVO instalado.
 *
 * No navegador o aviso chega pelo próprio navegador (Web Push + service
 * worker). Dentro do app a página corre numa WebView, e a WebView do Android
 * não tem a API de push do navegador — era por isso que a tela dizia "Este
 * navegador não suporta notificações push" no celular e funcionava no
 * computador. Lá quem recebe é a casca nativa, pelo Firebase, e o que
 * identifica o aparelho é um token.
 *
 * Este arquivo é só a ponte do lado da tela: pedir a permissão, pegar o
 * token e entregá-lo ao servidor (`saveAppPushToken`). Quem decide o aviso,
 * o texto e o link continua sendo o mesmo código de sempre.
 *
 * O plugin do Capacitor é importado DIRETO, não por `import()` tardio
 * (23/10/2026). O módulo dele é inofensivo no navegador — só chama
 * `registerPlugin`, que fora do app devolve um objeto que responde "não
 * implementado" —, e o carregamento tardio era um pedaço de JavaScript a
 * mais para buscar do servidor bem na hora de conferir o aparelho: um
 * pedido que não responde deixava a conferência pendurada para sempre, sem
 * erro e sem fim. O que decide se há plugin é `isNativeApp()`, não o
 * import.
 */

import { PushNotifications } from "@capacitor/push-notifications";

import { comPrazo } from "./com-prazo";
import { isNativeApp } from "./is-native-app";
import { anotar, motivo } from "./push-diagnostico";

export { isNativeApp } from "./is-native-app";

type Plugin = typeof PushNotifications;

/**
 * O PLUGIN — E POR QUE ESTA FUNÇÃO NÃO É `async` (23/10/2026).
 *
 * Ela era. E era ESSE o bug que girou o botão sem fim três entregas
 * seguidas: `await plugin()` NUNCA voltava, e nada depois dele chegava a
 * rodar — nem o pedido da permissão, nem o registro no Firebase. O caderno
 * de bordo entregou a prova: a única linha anotada era a da tela ("esperando
 * a permissão do Android"), e nenhuma de dentro deste arquivo.
 *
 * A causa é uma regra do JavaScript. O que o Capacitor devolve não é um
 * objeto comum: é um proxy que responde a QUALQUER nome de método, inclusive
 * `then`. Uma função `async` que devolve um valor com `then` acha que
 * devolveu outra promessa e chama esse `then` esperando a resposta — ou
 * seja, o navegador pedia ao Android um método chamado "then", que não
 * existe e nunca responde. A espera não tinha fim, e por isso prazo nenhum
 * dentro das outras funções resolvia: elas nem começavam.
 *
 * Função comum devolve o proxy como ele é. Regra que fica: plugin do
 * Capacitor nunca volta de `async` nem entra em `Promise.resolve`.
 */
function plugin(): Plugin | null {
  return isNativeApp() ? PushNotifications : null;
}

/**
 * Registra no Firebase e devolve o token deste aparelho.
 *
 * O `register()` do plugin não devolve o token: ele dispara o evento
 * `registration` quando o Firebase responde. Por isso a espera com prazo —
 * sem rede, o evento simplesmente não vem, e a tela não pode ficar pendurada.
 */
export async function nativePushToken(timeoutMs = 15000): Promise<string | null> {
  const push = plugin();
  if (!push) return null;

  // OUVIR ANTES DE PEDIR. `addListener` é assíncrono do lado nativo: chamar
  // `register()` sem esperar que os dois ouvintes estejam de pé é uma
  // corrida — quando o Firebase respondia rápido, o evento saía antes de
  // alguém estar ouvindo e a tela esperava o prazo inteiro à toa, mostrando
  // "Verificando suporte…" sem fim (relato do Leandro, 23/10/2026).
  let resolver: ((v: string | null) => void) | null = null;
  let resolvido: string | null | undefined;
  const finish = (v: string | null) => {
    if (resolvido !== undefined) return;
    resolvido = v;
    resolver?.(v);
  };

  // …MAS COM PRAZO. `addListener` fala com o lado nativo, e esperar por ele
  // sem prazo era outra espera sem fim: no aparelho do Leandro o botão
  // "Ativar notificações" girava para sempre, agora sem nem chegar ao
  // `register()`. Se a ponte não responder em 5 segundos, seguimos assim
  // mesmo — o ouvinte ainda pode subir e o `finish` continua valendo.
  anotar("montando os ouvintes do Firebase (addListener)");
  const ouvintes = await comPrazo(
    Promise.all([
      push.addListener("registration", (t: { value: string }) => {
        anotar("o Firebase devolveu o token deste aparelho");
        finish(t.value);
      }),
      push.addListener("registrationError", (e: unknown) => {
        anotar(`o Firebase recusou o registro: ${motivo(e)}`);
        finish(null);
      }),
    ]).then(
      () => "de pé" as const,
      (e) => {
        anotar(`addListener falhou: ${motivo(e)}`);
        return "falhou" as const;
      },
    ),
    5000,
    "sem resposta em 5s" as const,
  );
  anotar(`ouvintes: ${ouvintes}`);

  const espera = new Promise<string | null>((resolve) => {
    if (resolvido !== undefined) {
      resolve(resolvido);
      return;
    }
    resolver = resolve;
  });

  const timer = setTimeout(() => {
    anotar(`o registro no Firebase não respondeu em ${timeoutMs / 1000}s`);
    finish(null);
  }, timeoutMs);
  anotar("chamando register()");
  void push.register().then(
    () => anotar("register() aceito; esperando o token"),
    (e) => {
      anotar(`register() falhou: ${motivo(e)}`);
      finish(null);
    },
  );
  try {
    return await espera;
  } finally {
    clearTimeout(timer);
  }
}

export type NativePermission = "granted" | "denied" | "prompt";

/** O que o Android responde, no vocabulário desta tela. */
function comoPermissao(receive: string | undefined): NativePermission {
  return receive === "granted"
    ? "granted"
    : receive === "denied"
      ? "denied"
      : "prompt";
}

/** Uma pergunta ao Android sobre a permissão, com prazo curto. */
async function perguntarAoAndroid(
  push: Plugin,
  ms = 8000,
): Promise<NativePermission> {
  // Com prazo: perguntar ao Android é ida e volta pela ponte nativa, e
  // ponte que não responde deixava a tela pendurada. O que ele responde
  // (ou o erro que vem no lugar) fica anotado: é o fato que faltava no
  // relato para saber se a ponte sequer existe neste aparelho.
  const r = await comPrazo(
    push.checkPermissions().then(
      (v) => {
        anotar(`checkPermissions respondeu: ${JSON.stringify(v)}`);
        return v;
      },
      (e) => {
        anotar(`checkPermissions falhou: ${motivo(e)}`);
        throw e;
      },
    ),
    ms,
    { receive: "prompt" as const },
  );
  return comoPermissao(r.receive);
}

export async function nativePushPermission(): Promise<NativePermission> {
  const push = plugin();
  if (!push) return "prompt";
  try {
    return await perguntarAoAndroid(push);
  } catch {
    return "prompt";
  }
}

export async function requestNativePushPermission(): Promise<NativePermission> {
  const push = plugin();
  if (!push) return "prompt";
  try {
    // A JANELA DO ANDROID QUE NÃO VOLTA (relato do Leandro, 23/10/2026: o
    // aviso disse, com todas as letras, "passaram 30 segundos esperando a
    // permissão do Android"). O `requestPermissions()` do plugin devolve a
    // resposta pela ponte nativa, e no aparelho dele essa resposta
    // simplesmente não chega — nem "permitiu", nem "recusou".
    //
    // Então a tela parou de depender SÓ dela: pede a janela e, ao mesmo
    // tempo, PERGUNTA AO ANDROID de segundo em segundo o que ele já diz da
    // permissão. Quem responder primeiro vale. Assim um "Permitir" cuja
    // resposta se perdeu no caminho ainda conta, porque a pergunta seguinte
    // já encontra a permissão dada.
    anotar("pedindo a permissão ao Android (requestPermissions)");
    const pedido = push.requestPermissions().then(
      (r) => {
        anotar(`requestPermissions respondeu: ${JSON.stringify(r)}`);
        return comoPermissao(r.receive);
      },
      (e) => {
        anotar(`requestPermissions falhou: ${motivo(e)}`);
        throw e;
      },
    );

    const vigia = (async () => {
      for (let i = 0; i < 24; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        const v = await perguntarAoAndroid(push, 3000).catch(
          () => "prompt" as const,
        );
        if (v !== "prompt") return v;
      }
      anotar("24 perguntas ao Android e a permissão seguiu em 'prompt'");
      return "prompt" as const;
    })();

    const fim = await comPrazo(
      Promise.race([pedido, vigia]),
      26000,
      "prompt" as const,
    );
    anotar(`permissão depois do pedido: ${fim}`);
    return fim;
  } catch (e) {
    anotar(`pedido de permissão caiu: ${motivo(e)}`);
    return "prompt";
  }
}

/**
 * O aviso que chega com o app ABERTO.
 *
 * Com o app fechado quem desenha a notificação é o Android. Com ele aberto o
 * Firebase entrega calado — sem isto, o aviso simplesmente não aparece para
 * quem está com o sistema na mão.
 */
export async function onNativePushReceived(
  handler: (n: { title?: string; body?: string; url?: string }) => void,
): Promise<void> {
  const push = plugin();
  if (!push) return;
  try {
    await push.addListener("pushNotificationReceived", (n) => {
      const data = (n.data ?? {}) as Record<string, unknown>;
      handler({
        title: n.title ?? undefined,
        body: n.body ?? undefined,
        url: typeof data.url === "string" ? data.url : undefined,
      });
    });
  } catch {
    // Notificação é conforto: nada aqui pode derrubar a tela.
  }
}

/** O toque no aviso: a tela que ele deve abrir, se vier uma. */
export async function onNativePushTapped(
  handler: (url: string) => void,
): Promise<void> {
  const push = plugin();
  if (!push) return;
  try {
    await push.addListener("pushNotificationActionPerformed", (a) => {
      const data = (a.notification?.data ?? {}) as Record<string, unknown>;
      const url = typeof data.url === "string" ? data.url : null;
      // Só rota interna: o aviso vem do nosso servidor, mas abrir cegamente
      // o que vier escrito num campo de texto é porta aberta.
      if (url && url.startsWith("/") && !url.startsWith("//")) handler(url);
    });
  } catch {
    // idem
  }
}
